import { jwtDecode, JwtPayload } from "jwt-decode"

export default class Token {
	public readonly raw: string
	public readonly expiresAt: Date
	public readonly isMultiFactorRequired: boolean
	public readonly origin: TokenOrigin
	public readonly authenticationMethods: TokenAuthenticationMethod[]

	public get isMultiFactorAuthenticated(): boolean {
		return this.isPasskeyAuthenticated
	}

	public get isPasskeyAuthenticated(): boolean {
		return this.authenticationMethods.includes(TokenAuthenticationMethod.passkey)
	}

	public get isMultifactorSatisfied(): boolean {
		return !this.isMultiFactorRequired || this.isMultiFactorAuthenticated
	}

	public get isOriginPasskey(): boolean {
		return this.origin === TokenOrigin.passkey
	}

	public get isOriginPassword(): boolean {
		return this.origin === TokenOrigin.password
	}

	public get shortRaw(): string {
		return this.raw.slice(-5)
	}

	public constructor(rawToken: string) {
		this.raw = rawToken
		const decoded = jwtDecode<IDecodedPayload>(rawToken)
		this.expiresAt = decoded.exp !== undefined ? new Date(decoded.exp * 1000) : new Date()
		this.isMultiFactorRequired = decoded.fido2_required ?? false
		this.origin = decoded.origin ?? TokenOrigin.unknown
		this.authenticationMethods =
			decoded.amr !== undefined
				? Array.isArray(decoded.amr) ? decoded.amr : [decoded.amr]
				: []
	}

	public calculateIsExpiredNow(): boolean {
		return new Date() > this.expiresAt
	}

	public getMillisecondsUntilExpiration(): number {
		return this.expiresAt.getTime() - Date.now()
	}
}

interface IDecodedPayload extends JwtPayload {
	fido2_required: boolean
	origin: TokenOrigin
	amr: TokenAuthenticationMethod | TokenAuthenticationMethod[]
}

export enum TokenOrigin {
	password = "password",
	passkey = "passkey",
	refreshToken = "refresh token",
	unknown = "unknown"
}

export enum TokenAuthenticationMethod {
	password = "pwd",
	passkey = "fido2",
}
